// The signed-in person's own profile in the console: name, phone, where they work and as what,
// and the way to their sign-in and security settings.
import { AlertsCard } from "./AlertsCard.tsx";
import { useState } from "react";
import { Avatar, Badge, Button, Card, Table, TextField } from "@vavr/ui";
import { api, navigate } from "../shared/index.ts";
import { useAction, useConsole } from "./data.tsx";
import { ROLE_LABEL } from "./common.tsx";

export function Profile() {
  const { me } = useConsole();
  const act = useAction();
  const [name, setName] = useState(me.account.name);
  const [phone, setPhone] = useState<string>(me.account.phone ?? "");
  const siteName = (id: string | null, org: string | null) => (id ? me.sites.find((s) => s.id === id)?.name ?? "A site" : me.sites.find((s) => s.org_id === org)?.name ? "Every site" : "");
  const roles = me.memberships.filter((m) => m.role !== "guardian");
  const phoneOk = !phone || /^\+1\d{10}$/.test(phone);
  return (
    <div className="pmu-stack pmu-c-profile-page">
      <section className="pmu-c-profile-hero">
        <Avatar name={me.account.name} kind="adult" size="xl" />
        <div>
          <h2>{me.account.name}</h2>
          <p className="pmu-muted">{me.account.email}</p>
          <div className="pmu-row">{[...new Set(roles.map((m) => m.role))].map((r) => <Badge key={r} tone={r.endsWith("admin") ? "brand" : "neutral"}>{ROLE_LABEL[r] ?? r}</Badge>)}</div>
        </div>
      </section>
      <div className="pmu-c-profile-grid">
        <Card title="Your details" description="Shown to colleagues on rosters, run sheets and messages.">
          <form className="pmu-stack" onSubmit={(e) => { e.preventDefault(); void act.run(() => api("PATCH", "/api/v1/me", { name: name.trim(), phone: phone.trim() || null }), { success: "Saved.", invalidate: ["/api/v1/me"] }); }}>
            <TextField label="Name" required maxLength={120} value={name} onChange={(e) => setName(e.target.value)} />
            <TextField label="Email" value={me.account.email} readOnly hint="Your sign-in address. Ask an administrator to change it." />
            <TextField label="Mobile phone" type="tel" placeholder="+15125550100" value={phone} onChange={(e) => setPhone(e.target.value)} error={phoneOk ? undefined : "A US number with country code, like +15125550100."} />
            <div><Button type="submit" loading={act.busy} disabled={!name.trim() || !phoneOk}>Save changes</Button></div>
          </form>
        </Card>
        <AlertsCard />
        <Card title="Sign-in and security" description="Password, passkeys and the second factor administrators must use.">
          <div className="pmu-stack">
            <p>{me.account.totp ? <Badge tone="success">Second factor on</Badge> : <Badge tone="warning">No second factor</Badge>}</p>
            <div><Button variant="secondary" icon="lock" onClick={() => navigate("/account/security")}>Open sign-in and security</Button></div>
          </div>
        </Card>
      </div>
      <Card title="Where you work" flush>
        <Table caption="Your roles" hideCaption rowKey={(m) => `${m.role}-${m.org_id}-${m.site_id}`} rows={roles} empty="No roles."
          columns={[
            { key: "role", header: "Role", rowHeader: true, render: (m) => ROLE_LABEL[m.role] ?? m.role },
            { key: "where", header: "Where", render: (m) => siteName(m.site_id, m.org_id) },
          ]} />
      </Card>
    </div>
  );
}
